// ============================================================
// useAuditFeed — OLIVIUM Signal stream.
//
// Demo: seeds 10 plausible events, then synthesizes one new
//       event every 3s so the feed feels operational.
// Live: pulls last 100 from olv_audit_trail and subscribes to
//       INSERTs via Supabase Realtime.
//
// Always returns derived stats: { total, criticals, warnings, denials }.
// Never throws — missing table just yields an empty list.
// ============================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { getRuntime } from './_mode'
import type { AuditEvent, AuditFeedStats } from './types'
import { captureHookFailure, recordHookOutcome, withSpan } from '../observability/sentry'

const SEVERITIES: Array<AuditEvent['severity']> = ['info', 'info', 'info', 'warning', 'critical']
const ACTIONS = ['access_granted', 'session_started', 'policy_check', 'chain_commit', 'device_seen', 'audit_append']
const OUTCOMES = ['success', 'success', 'success', 'denied']
const ACTORS = ['system', 'law-pocket', 'square-shell', 'olivium', 'prince-hall']

function normalizeAuditEvent(event: AuditEvent): AuditEvent {
  const chainHash = event.chain_hash ?? event.event_hash
  return {
    ...event,
    chain_hash: chainHash,
    event_hash: event.event_hash ?? chainHash,
  }
}

function buildDemoEvent(seq: number): AuditEvent {
  const severity = SEVERITIES[Math.floor(Math.random() * SEVERITIES.length)]
  const action = ACTIONS[Math.floor(Math.random() * ACTIONS.length)]
  const outcome = OUTCOMES[Math.floor(Math.random() * OUTCOMES.length)]
  const actor = ACTORS[Math.floor(Math.random() * ACTORS.length)]
  const rand = () => Math.random().toString(36).slice(2, 12)
  const chainHash = `0x${rand()}${rand()}`
  return {
    id: `demo-event-${seq}-${Date.now()}`,
    sequence: seq,
    event_type: action,
    action,
    actor_id: actor,
    actor_role: actor,
    resource_type: action.includes('session') ? 'session' : 'resource',
    resource_id: `res-${Math.floor(Math.random() * 1000)}`,
    outcome,
    severity,
    detail: { note: `${action} issued by ${actor}` },
    event_hash: chainHash,
    chain_hash: chainHash,
    previous_hash: `0x${rand()}${rand()}`,
    created_at: new Date().toISOString(),
  }
}

export function useAuditFeed() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [authRequired, setAuthRequired] = useState(false)
  // True when a LIVE olv_audit_trail read failed (missing table, query/network
  // error). Lets consumers distinguish a real empty trail from a failed read,
  // so a failure is surfaced honestly instead of read as "0 durable events".
  const [loadError, setLoadError] = useState(false)
  const demoTimer = useRef<number | null>(null)
  const seqRef = useRef(0)

  const pushEvent = useCallback((event: AuditEvent) => {
    setEvents((prev) => [normalizeAuditEvent(event), ...prev].slice(0, 100))
  }, [])

  useEffect(() => {
    let cancelled = false
    let channel: RealtimeChannel | null = null
    const { live, supabase } = getRuntime()

    async function load() {
      if (!live || !supabase) {
        const seed = Array.from({ length: 10 }).map(() => buildDemoEvent(++seqRef.current))
        if (!cancelled) {
          setEvents(seed)
          setAuthRequired(false)
          setLoadError(false)
          setLoading(false)
        }
        if (typeof window !== 'undefined') {
          demoTimer.current = window.setInterval(() => {
            pushEvent(buildDemoEvent(++seqRef.current))
          }, 3000)
        }
        recordHookOutcome('useAuditFeed', 'demo_fallback', { rows: seed.length })
        return
      }

      const startedAt = Date.now()
      try {
        const { data: authData } = await supabase.auth.getSession()
        if (!authData.session) {
          if (!cancelled) {
            setEvents([])
            setAuthRequired(true)
            setLoadError(false)
            setLoading(false)
          }
          return
        }

        const { data, error } = await withSpan('olv_audit_trail.select', 'db.query', async () =>
          supabase
            .from('olv_audit_trail')
            .select('*')
            .order('sequence', { ascending: false })
            .limit(100)
        )

        if (cancelled) return
        const durationMs = Date.now() - startedAt

        if (error) {
          const message = error.message || ''
          const missing =
            /relation .* does not exist/i.test(message) ||
            /could not find the table/i.test(message) ||
            error.code === '42P01'
          setEvents([])
          setLoadError(true)
          captureHookFailure(
            'useAuditFeed',
            missing ? 'missing_dependency' : 'live_error',
            error,
            { table: 'olv_audit_trail', durationMs, message }
          )
        } else {
          const rows = ((data as AuditEvent[]) || []).map(normalizeAuditEvent)
          setEvents(rows)
          setAuthRequired(false)
          setLoadError(false)
          recordHookOutcome('useAuditFeed', 'live_success', {
            table: 'olv_audit_trail',
            rows: rows.length,
            durationMs,
          })
        }
      } catch (err) {
        if (cancelled) return
        setEvents([])
        setLoadError(true)
        captureHookFailure('useAuditFeed', 'live_error', err, { table: 'olv_audit_trail' })
      }
      setLoading(false)

      try {
        channel = supabase
          .channel('square-shell-audit')
          .on(
            'postgres_changes' as any,
            { event: 'INSERT', schema: 'public', table: 'olv_audit_trail' },
            (payload: { new: AuditEvent }) => pushEvent(normalizeAuditEvent(payload.new))
          )
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') {
              recordHookOutcome('useAuditFeed', 'live_success', {
                message: 'realtime subscribed',
                table: 'olv_audit_trail',
              })
            } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
              captureHookFailure(
                'useAuditFeed',
                'realtime_failure',
                new Error(`audit realtime ${status}`),
                { table: 'olv_audit_trail', message: status }
              )
            }
          })
      } catch (err) {
        captureHookFailure('useAuditFeed', 'realtime_failure', err, { table: 'olv_audit_trail' })
      }
    }

    load()
    return () => {
      cancelled = true
      if (demoTimer.current && typeof window !== 'undefined') {
        window.clearInterval(demoTimer.current)
      }
      if (channel && supabase) supabase.removeChannel(channel)
    }
  }, [pushEvent])

  const stats: AuditFeedStats = useMemo(() => {
    const total = events.length
    const criticals = events.filter((e) => e.severity === 'critical').length
    const warnings = events.filter((e) => e.severity === 'warning').length
    const denials = events.filter((e) => e.outcome === 'denied' || e.outcome === 'blocked').length
    return { total, criticals, warnings, denials }
  }, [events])

  return { events, stats, loading, authRequired, loadError }
}
